import { React, useState, useMemo, useContext } from 'react'
import { Link, useLocation } from 'react-router-dom';
import moment from "moment";
import { routeNames } from '../../routeSegments';
import { FundingContext } from '../../Contexts/FundingDataProvider';
import OverflowToolTip from '../shared/OverflowToolTip';
import Paginate from '../Paginate';
import Constants from '../../Constants';
import { usePagination, useSortBy, useTable } from 'react-table';
import SortAscendingIcon from 'mdi-react/SortAscendingIcon';
import SortDescendingIcon from 'mdi-react/SortDescendingIcon';
import SortIcon from 'mdi-react/SortIcon';
import LockIcon from 'mdi-react/LockIcon';
import ConfirmPopup from '../../Utils/ConfirmPopup';



/* The above code is a React component written in JavaScript. It is a table component that displays
investment data. It receives props such as menu, data, companyFundingId, and fundingData. */
const FundingTableInvestment = ({ menu, data, companyFundingId, fundingData }) => {

    const { setpevcData } = useContext(FundingContext);
    const location = useLocation();
    const [alert, setalert] = useState({ message: "", show: false });

    /**
     * The function `onConfirmation` sets the `show` property of the `alert` state to `false` and
     * clears the `message` property.
     */
    const onConfirmation = (flag) => {
        setalert({ show: false, message: "" })
    }

    /**
     * The function `onBlurColumnClick` sets an alert message with a subscription message and displays
     * it.
     */
    const onBlurColumnClick = (cell) => {
        setalert({ show: true, message: Constants.peSubscriptionMsg, isAlertOnly: true, closeBtnName: "OK" })
    }

    /* The above code is defining a custom sorting function using the `useMemo` hook in React. The
    function takes in two rows (`rowA` and `rowB`), a column ID, and a boolean value `desc`
    indicating whether the sorting should be in descending order. */
    const customStringSortMemo = useMemo(() => {
        return (rowA, rowB, columnId, desc) => {
            const defaultVal = desc ? '--' : 'A';
            // console.log(rowA?.values[columnId]?.roundName.toLowerCase(),'lower case');
            return (rowA?.values[columnId]?.roundName ?? defaultVal).localeCompare(rowB?.values[columnId]?.roundName ?? defaultVal);
        }
    }, []);


    /* The above code is defining a custom sorting function called `dealDateSort` using the `useMemo`
    hook in React. This sorting function is used to compare two rows (`rowA` and `rowB`) based on a
    specific column (`columnId`) containing date values. */
    const dealDateSort = useMemo(() => {
        return (rowA, rowB, columnId, desc) => {
            return moment.utc(rowA?.values[columnId].date).diff(moment.utc(rowB?.values[columnId].date))
        }
    }, [])

    /* The above code is defining a custom sorting function called `investorSort` using the `useMemo`
    hook in React. This sorting function takes in two rows (`rowA` and `rowB`), a column ID, and a
    boolean flag `desc` indicating whether the sorting should be in descending order. */
    const investorSort = useMemo(() => {
        return (rowA, rowB, columnId, desc) => {
            const defaultVal = desc ? '--' : 'A';

            const valueA = rowA?.values[columnId].map(data => data.investor_name).join("")
            const valueB = rowB?.values[columnId].map(data => data.investor_name).join("")

            return (valueA ?? defaultVal).localeCompare(valueB ?? defaultVal);
        }
    }, [])

    /* The above code is defining an array of columns for a table in a React component. Each column has
    properties such as id, Header (displayed column name), accessor (data key), className (CSS
    classes), headerspanClassName (CSS classes for the header span), headerClassName (CSS classes
    for the header), Cell (rendering logic for the cell), sortType (custom sorting logic), and more.
    The columns are generated dynamically based on the values of menu and companyFundingId
    variables. The code also includes a helper function addCommasToNumber to format numbers with
    commas. */
    const columns = useMemo(() => {
        let blurColumns;

        const allowedModulesStr = localStorage.getItem(Constants.localStorageKeys.allowedModules);
        const allowedModulesList = JSON.parse(allowedModulesStr) ?? [];

        if (allowedModulesList.includes("pe")) {
            blurColumns = false;
        } else {
            blurColumns = true;
        }

        const addCommasToNumber = (number) => {
            return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          };

        return [
            {
                id: "round",
                Header: "Round",
                accessor: "round",
                className: "clickable w-[11%] text-left sticky left-0 lg:relative bg-white",
                headerspanClassName: "",
                headerClassName: "text-left sticky left-0 lg:relative  w-[3rem] bg-[#EDEDED] ",
                Cell: (cell) => {
                    if (cell.value == Constants.restrictedPlaceholder) {
                        return cell.value
                    }
                    return <OverflowToolTip className="w-[9rem] lg:w-auto" component={<Link
                        onClick={(e) => {
                            if (menu == "cfs") {
                                if (!allowedModulesList.includes("pe") && !allowedModulesList.includes("vc")) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onBlurColumnClick();
                                }
                            }else if(cell.value.restricted == Constants.restrictedPlaceholder){
                                e.preventDefault();
                                e.stopPropagation();
                                onBlurColumnClick();
                            }
                        }}
                        rel="noopener noreferrer"
                        state={{ prevPath: location.state?.prevPath ?? "" }}
                         to={menu == "cfs"
                            ? allowedModulesList.includes("pe")
                                ? routeNames.peCompanyInvestmentDealById(companyFundingId, cell.value.id)
                                : routeNames.vcCompanyInvestmentDealById(companyFundingId, cell.value.id)
                            : menu == "vc"
                                ? routeNames.vcCompanyInvestmentDealById(companyFundingId, cell.value.id)
                                : menu == "ma"
                                    ? routeNames.peCompanyInvestmentDealById(companyFundingId, cell.value.id)
                                    : routeNames.peCompanyInvestmentDealById(companyFundingId, cell.value.id)}>
                        {cell.value.roundName ?? "--"}
                    </Link>} />
                },
                sortType: customStringSortMemo,
            },

            {
                id: "dealDate",
                Header: "Deal Date",
                accessor: "dealDate",
                className: "w-[9%]",
                headerspanClassName: "",
                headerClassName: "",
                Cell: (cell) => {
                    const date = cell?.value?.date ? moment(cell.value.date).format('MMM YYYY') : ""; // Check if date is not null

                    if (cell.value == Constants.restrictedPlaceholder) {
                        return cell.value
                    }
                    return <Link
                        onClick={(e) => {
                            if (menu == "cfs") {
                                if (!allowedModulesList.includes("pe") && !allowedModulesList.includes("vc")) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onBlurColumnClick();
                                }
                            }else if(cell.value.restricted == Constants.restrictedPlaceholder){
                                e.preventDefault();
                                e.stopPropagation();
                                onBlurColumnClick();
                            }
                        }}
                        state={{ prevPath: location.state?.prevPath ?? "" }}
                        to={menu == "cfs"
                            ? allowedModulesList.includes("pe")
                                ? routeNames.peCompanyInvestmentDealById(companyFundingId, cell.value.id)
                                : routeNames.vcCompanyInvestmentDealById(companyFundingId, cell.value.id)
                            : menu == "vc"
                                ? routeNames.vcCompanyInvestmentDealById(companyFundingId, cell.value.id)
                                : menu == "ma"
                                    ? routeNames.peCompanyInvestmentDealById(companyFundingId, cell.value.id)
                                    : routeNames.peCompanyInvestmentDealById(companyFundingId, cell.value.id)}>
                        {date}
                    </Link>
                },
                sortType: dealDateSort

            },
            {
                Header: "Deal Amount (US$ M)",
                accessor: "dealAmountInUS",
                className: "text-right  w-[16%] pr-[2.7rem] md:pr-[2.7rem] 2xl:pr-[2.4rem] lg:pr-[2.75rem]",
                headerspanClassName: "ml-auto ",
                headerClassName: "",
                Cell: (cell) => {
                    return addCommasToNumber(cell?.value)
                },


            },
            {
                Header: "Investors",
                accessor: "investors",
                className: "clickable w-[17%]",
                headerspanClassName: " ",
                headerClassName: "",
                sortType: investorSort,
                Cell: (cell) => {
                    if (cell.value == Constants.restrictedPlaceholder) {
                        return cell.value
                    }

                    return <OverflowToolTip className="w-[20rem] lg:w-auto max-w-[18rem] lg:max-w-[15rem] 2xl:max-w-[25rem] lg:overflow-x-clip lg:overflow-ellipsis" component={
                        cell.value?.map((inv, index) => (
                            <Link onClick={(e) => {
                                if (menu == "cfs" || menu == "ma") {
                                    if (!allowedModulesList.includes("pe") && !allowedModulesList.includes("vc")) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onBlurColumnClick();
                                    }
                                }
                            }} rel="noopener noreferrer" className='hover:text-[#BC8B20]'
                                state={{ cameFrom: location.pathname }} to={menu == "cfs" || menu == "ma"
                                    ? allowedModulesList.includes("pe")
                                        ? routeNames.peInvestorById(inv.investor_id)
                                        : routeNames.vcInvestorById(inv.investor_id)
                                    : menu == "vc"
                                        ? routeNames.vcInvestorById(inv.investor_id)
                                        : routeNames.peInvestorById(inv.investor_id)}>
                                {`${inv.investor_name}${index == cell.value?.length - 1 ? "" : ", "}`}
                            </Link>))

                    } />
                },

            },
            {
                Header: "Exit Status",
                accessor: "exitStatus",
                className: "w-[10rem]",
                // headerspanClassName: "ml-auto ",
                headerClassName: "w-[10rem]",


            },
            {
                Header: "Post Money Valuation (INR Cr)/($M)",
                accessor: "postMoneyValuation",
                className: `text-right w-[0%]  pr-[2.7rem]`,
                headerspanClassName: "ml-auto ",
                headerClassName: "",
                Cell: (cell) => {
                    return addCommasToNumber(cell?.value)
                },
                // disableSortBy: blurColumns,
                // blurColumns: blurColumns,
                // Cell: ({ value }) => {
                //     if (!blurColumns) {
                //         return value;
                //     }
                //     return <div className='cursor-pointer' onClick={(e) => onBlurColumnClick(value)}>{value ?? "XXX"}</div>;
                // }
            },
            // {
            //     Header: "($M)",
            //     accessor: "postmoneyValuation",
            //     className: `lg:px-0 w-[1%] text-right lg:pr-[2.5rem]`,
            //     headerspanClassName: "ml-auto",
            //     headerClassName: "w-[13%]",
             
            // },
            {
                Header: "Revenue Multiple",
                accessor: "revenueMultiple",
                className: `text-right  w-[15%] pr-[2.5rem]`,
                headerspanClassName: "ml-auto ",
                headerClassName: "mr-auto",
                Cell: (cell) => {
                    return addCommasToNumber(cell?.value)
                },
                // disableSortBy: blurColumns,
                // blurColumns: blurColumns,
                // Cell: ({ value }) => {
                //     if (!blurColumns) {
                //         return value;
                //     }
                //     return <div className='cursor-pointer' onClick={(e) => onBlurColumnClick(value)}>{value ?? "XXX"}</div>;
                // }
            },
        ];
    }, [menu, companyFundingId])

    /* The above code is a JavaScript React code snippet. It defines a function that uses the `useMemo`
    hook to memoize the calculation of `rowData`. */
    const rowData = useMemo(() => {
        let rows = [];
        
        data?.forEach((investment, i) => {
            rows.push({
                round: investment?.Round == Constants.restrictedPlaceholder ? investment?.Round : { roundName: investment?.Round, id: investment.Deal_Id,  restricted: investment.Revenue_Multiple },
                dealDate: investment?.Deal_Date == Constants.restrictedPlaceholder ? investment?.Deal_Date : { date: investment?.Deal_Date, id: investment.Deal_Id, restricted: investment.Revenue_Multiple },
                dealAmountInUS: investment.Deal_Amount == 0 ? "" : investment.Deal_Amount,

                investors: investment?.Investors ?? [],
                exitStatus: investment.Exit_Status == 0 ? "" : investment.Exit_Status,
                // postMoneyValuation:(investment.Post_Money_Valuation ? (investment.Post_Money_Valuation >0 ? investment.Post_Money_Valuation +"/":"")
                // +( investment.Post_Money_Valuation ? (investment.Post_Money_Valuation >0 ?  parseFloat(investment.Post_Money_Valuation * usdConvert).toFixed(2) :"") :""):""),
                // postmoneyValuation: investment.Post_Money_Valuation == Constants.restrictedPlaceholder ? investment.Post_Money_Valuation : (investment.Post_Money_Valuation == 0 ? "" : parseFloat(investment.Post_Money_Valuation * usdConvert).toFixed(2)),
                revenueMultiple: investment.Revenue_Multiple == 0 ? "" : investment.Revenue_Multiple,
                postMoneyValuation:investment.Post_Money_Valuation
                
            })
        })


        setpevcData(rows.length == 0 ? null : rows)

        return rows;
    }, [data])

    /* The above code is using the `useMemo` hook in React to create a memoized value called
    `initialSort`. */
    const initialSort = useMemo(() => {
        if (fundingData?.data?.isSort == false) {
            return []
        } else {
            return [
                {
                    id: 'dealDate',
                    desc: true
                }
            ]
        }
    }, [fundingData?.data?.isSort]);

    /* The above code is using the `useTable` hook from the React-Table library to create a table
    instance. */
    const tableInstance = useTable({
        columns,
        data: rowData,
        disableSortRemove: true,
        initialState: {
            pageSize: Constants.noOfRowPerPage,
            pageIndex: 0,
            sortBy: initialSort,
        },
    },
        useSortBy,
        usePagination,
    );

    /* The above code is using destructuring assignment to extract specific properties and methods from
    the `tableInstance` object. These extracted properties and methods are then used in a React
    component to render a table. */
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        page,
        prepareRow,
        gotoPage,
        state: { pageIndex, pageSize }
    } = tableInstance;


    return (

        <div className="space-y-6">
            <div className="bg-white">
                <>

                    <div className='overflow-x-auto scrollbar relative'>
                        {/* <div className='table absolute w-full h-full'>
                            <div className='table-row '>
                                <div className='table-cell invisible pointer-events-none whitespace-nowrap px-5 py-4 w-[10%]'></div>
                                <div className='table-cell bg-slate-300'>
                                    <LockIcon />
                                </div>
                            </div>
                        </div> */}
                        {/* <div className='w-full h-full flex absolute justify-end'>
                            <div className='w-[40%] bg-slate-200'>

                            </div>
                        </div> */}
                        {rows?.length > 0 ? 
                        <table className=" datatable w-full" {...getTableProps()}>

                            <thead className="text-[12pt] leading-[15pt] bg-[#EDEDED] lg:text-[13px] lg:leading-[15px] text-[#666666] font-semibold py-4 px-0 text-center ">
                                {
                                    headerGroups.map(headerGroup => (

                                        <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                                            {
                                                headerGroup.headers.map(column => (
                                                    <th {...column.getHeaderProps([
                                                        {
                                                            className: `py-3.5 px-5 whitespace-nowrap ${column.headerClassName}`,
                                                            style: column.headerStyle,
                                                        },
                                                        column.getSortByToggleProps(),
                                                    ])} title="">
                                                        <div className="flex">
                                                            <span
                                                                {...column.getHeaderProps([
                                                                    {
                                                                        className: `mr-1 ${column.headerspanClassName}`,
                                                                    },

                                                                ])}
                                                            >{column.render('Header')}</span>
                                                            {/* <span className='mr-1'>{column.render('Header')}</span> */}
                                                            {column.disableSortBy ? "" : column.isSorted
                                                                ? column.isSortedDesc
                                                                    ? <SortAscendingIcon size={16} className="mt-[0.15rem] lg:mt-0" />
                                                                    : <SortDescendingIcon size={16} className="mt-[0.15rem] lg:mt-0" />
                                                                : <SortIcon size={16} className="mt-[0.15rem] lg:mt-0" />}
                                                        </div>
                                                    </th>
                                                ))
                                            }
                                        </tr>
                                    ))
                                }
                            </thead>

                            <tbody {...getTableBodyProps()} className="relative text-[12pt] leading-[16pt] lg:text-[14px] font-sans_book_body text-left">

                                {
                                // page.length == 0
                                //     ? <tr>
                                //         <td colspan="8">
                                //             <div className="h-[4rem] mx-auto  lg:text-[15px] text-[15px] text-[#333333] relative mt-[3rem] text-center">
                                //                 No data found
                                //             </div>
                                //         </td>
                                //     </tr>
                                //     : 
                                    page.map(row => {
                                        prepareRow(row)
                                        return (
                                            <tr {...row.getRowProps()} className="border-b">
                                                {

                                                    row.cells.map(cell => {
                                                        console.log("cell.column.renderCol", cell.value);
                                                        if (cell.column.blurColumns || cell.value == Constants.restrictedPlaceholder) {
                                                            return (<td onClick={onBlurColumnClick} className=' relative whitespace-nowrap cursor-pointer' {...cell.getCellProps()}>
                                                                <div className='blur-sm bg-[#DCDCDC] flex justify-center select-none w-full h-full px-5 py-4'>{Constants.restrictedPlaceholder}</div>
                                                                <div className='absolute h-full w-full flex items-center justify-center top-0 bottom-0'>
                                                                    <LockIcon size={16} color='#696969'/>
                                                                </div>
                                                            </td>)
                                                        }
 
                                                        return (
                                                            <td colSpan={cell.column.colspan} rowSpan={cell.column.rowspan} {...cell.getCellProps([
                                                                {
                                                                    className: `data-td-animate lg:whitespace-nowrap px-5 py-4 ${cell.column.className}`,
                                                                    style: cell.column.style,
                                                                },
                                                            ])}>
                                                                {
                                                                    cell.value ? cell.render('Cell') : " "
                                                                }
                                                            </td>


                                                        )
                                                    })}
                                            </tr>

                                        )
                                    })}
                            </tbody>
                        </table>
                        :   
                        <>
                            <div className="h-[3rem] mx-auto  lg:text-[15px] text-[15px] text-[#333333] relative mt-[1rem] text-center">
                                No data found
                            </div>    
                        </>
                        }
                    </div>
                    {rows?.length <= Constants.noOfRowPerPage
                        ? <></>
                        : <Paginate
                            currentPage={pageIndex + 1}
                            totalCount={rows?.length}
                            pageSize={pageSize}
                            onPageChange={(page) => {
                                gotoPage(page - 1);
                            }}
                        />}
                    {<ConfirmPopup {...alert} onChange={onConfirmation} />}
                </>
            </div>
        </div>
    )
}

export default FundingTableInvestment